import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosAuth from '../../config/axios';
import { ESTADO_CITA, ASISTENCIA, Chip, fecha, hora, ahoraLocal, estilos as s } from './comun';

// Piezas de la agenda de orientación que usan la Agenda y la pantalla del caso.

const DURACIONES = [15, 30, 45, 60, 90, 120];

// "2026-10-01T09:00" + 45 → "2026-10-01T09:45" (hora local)
export function sumarMinutos(inicio, minutos) {
  const d = new Date(inicio);
  d.setMinutes(d.getMinutes() + minutos - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

// Date o texto de la API → "AAAA-MM-DDTHH:mm" local, para <input type="datetime-local">
export function aInputLocal(f) {
  const d = new Date(f);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function duracionDe(cita) {
  if (!cita?.fin) return 30;
  const min = Math.round((new Date(cita.fin) - new Date(cita.inicio)) / 60000);
  return DURACIONES.includes(min) ? min : 30;
}

// Envía y, si el servidor avisa de un cruce de horario, pide confirmación
function useEnvioConCruce() {
  const [cruce, setCruce] = useState(false);
  async function enviar(fn, forzar) {
    try {
      await fn(forzar);
      setCruce(false);
      return true;
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.cruce) { setCruce(true); return false; }
      throw err;
    }
  }
  return { cruce, setCruce, enviar };
}

// ─── Nueva cita ────────────────────────────────────────────────────────────────
export function CitaModal({ casoFijo, esLider, portalFamilia, onCerrar, onGuardada }) {
  const [tipos, setTipos] = useState([]);
  const [casos, setCasos] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [f, setF] = useState({
    caso_id: casoFijo?.id || '', tipo_id: '', inicio: sumarMinutos(ahoraLocal().slice(0, 11) + '08:00', 24 * 60).slice(0, 16),
    duracion: 30, lugar: '', participantes: '', visible_familia: false, profesional_id: '',
  });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const { cruce, setCruce, enviar } = useEnvioConCruce();
  const set = (campo) => (e) => { setCruce(false); setF(x => ({ ...x, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })); };

  useEffect(() => {
    axiosAuth.get('/api/bienestar/catalogos?tipo=tipo_cita').then(r => setTipos((r.data.data || []).filter(t => t.activo))).catch(() => {});
    if (!casoFijo) axiosAuth.get('/api/bienestar/casos?estado=abiertos').then(r => setCasos(r.data.data || [])).catch(() => {});
    if (esLider) axiosAuth.get('/api/bienestar/equipo/miembros').then(r => setMiembros(r.data.data || [])).catch(() => {});
  }, [casoFijo, esLider]);

  async function guardar(forzar = false) {
    setError('');
    if (!f.tipo_id) return setError('Selecciona el tipo de cita');
    setOcupado(true);
    try {
      const ok = await enviar((fz) => axiosAuth.post('/api/bienestar/citas', {
        caso_id: f.caso_id ? parseInt(f.caso_id) : null,
        tipo_id: parseInt(f.tipo_id), inicio: f.inicio, fin: sumarMinutos(f.inicio, parseInt(f.duracion)),
        lugar: f.lugar, participantes: f.participantes.split(',').map(p => p.trim()).filter(Boolean),
        visible_familia: !!f.caso_id && f.visible_familia,
        profesional_id: f.profesional_id ? parseInt(f.profesional_id) : undefined,
        forzar: fz || undefined,
      }), forzar);
      if (ok) onGuardada();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo agendar la cita');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <div style={s.fondoModal} onClick={onCerrar} />
      <div style={s.modal} role="dialog" aria-label="Nueva cita">
        <h3 style={s.modalTitulo}>Nueva cita de orientación</h3>
        {casoFijo ? (
          <p style={es.contexto}>Caso de <strong>{casoFijo.estudiante}</strong></p>
        ) : (
          <div style={s.campo}>
            <label style={s.label}>Caso (opcional)</label>
            <select style={s.input} value={f.caso_id} onChange={set('caso_id')}>
              <option value="">Sin caso (p. ej. reunión con docentes)</option>
              {casos.map(c => <option key={c.id} value={c.id}>{c.estudiante}{c.grado ? ` · ${c.grado}° ${c.grupo}` : ''}</option>)}
            </select>
          </div>
        )}
        <div style={es.fila2}>
          <div style={s.campo}>
            <label style={s.label}>Tipo *</label>
            <select style={s.input} value={f.tipo_id} onChange={set('tipo_id')}>
              <option value="">— Selecciona —</option>
              {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          {esLider && miembros.length > 1 && (
            <div style={s.campo}>
              <label style={s.label}>Orientador</label>
              <select style={s.input} value={f.profesional_id} onChange={set('profesional_id')}>
                <option value="">Yo</option>
                {miembros.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={es.fila2}>
          <div style={s.campo}>
            <label style={s.label}>Fecha y hora *</label>
            <input type="datetime-local" style={s.input} value={f.inicio} onChange={set('inicio')} required />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Duración</label>
            <select style={s.input} value={f.duracion} onChange={set('duracion')}>
              {DURACIONES.map(d => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60} h`}</option>)}
            </select>
          </div>
        </div>
        <div style={s.campo}>
          <label style={s.label}>Lugar</label>
          <input style={s.input} maxLength={150} value={f.lugar} onChange={set('lugar')} placeholder="Ej.: Oficina de orientación" />
        </div>
        <div style={s.campo}>
          <label style={s.label}>Participantes</label>
          <input style={s.input} value={f.participantes} onChange={set('participantes')} placeholder="Separados por coma. Ej.: Estudiante, Madre" />
        </div>
        {portalFamilia && f.caso_id && (
          <label style={es.check}>
            <input type="checkbox" checked={f.visible_familia} onChange={set('visible_familia')} />
            Visible para la familia (el acudiente verá la fecha, la hora y el lugar, y recibirá un aviso)
          </label>
        )}
        {cruce && (
          <div style={es.aviso}>
            Ya tienes una cita programada que se cruza con ese horario.
            <button onClick={() => guardar(true)} disabled={ocupado} style={{ ...s.btnSecundario, marginTop: '8px', display: 'block' }}>Agendar de todos modos</button>
          </div>
        )}
        {error && <div style={s.errorBox}>{error}</div>}
        <div style={s.botones}>
          <button onClick={onCerrar} style={s.btnSecundario}>Cancelar</button>
          <button onClick={() => guardar(false)} disabled={ocupado} style={s.btnPrimario}>{ocupado ? 'Guardando...' : 'Agendar'}</button>
        </div>
      </div>
    </>
  );
}

// ─── Detalle y acciones de una cita ───────────────────────────────────────────
export function PanelCita({ cita, onCerrar, onCambio, desdeCaso = false }) {
  const navigate = useNavigate();
  const [modo, setModo] = useState(null);          // null | 'reprogramar' | 'cancelar'
  const [nuevoInicio, setNuevoInicio] = useState(aInputLocal(cita.inicio));
  const [duracion, setDuracion] = useState(duracionDe(cita));
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const { cruce, setCruce, enviar } = useEnvioConCruce();
  const gestionable = cita.puede_gestionar !== false;

  async function accion(fn, textoError) {
    setOcupado(true); setError('');
    try {
      const ok = await fn();
      if (ok !== false) { onCambio(); onCerrar(); }
    } catch (err) {
      setError(err.response?.data?.error || textoError);
    } finally {
      setOcupado(false);
    }
  }

  const marcar = (valor) => accion(() => axiosAuth.post(`/api/bienestar/citas/${cita.id}/asistencia`, { asistencia: valor }), 'No se pudo registrar la asistencia');
  const reprogramar = (forzar) => accion(() => enviar((fz) => axiosAuth.post(`/api/bienestar/citas/${cita.id}/reprogramar`, {
    inicio: nuevoInicio, fin: sumarMinutos(nuevoInicio, parseInt(duracion)), forzar: fz || undefined,
  }), forzar), 'No se pudo reprogramar la cita');
  const cancelar = () => accion(() => axiosAuth.post(`/api/bienestar/citas/${cita.id}/cancelar`, { motivo }), 'No se pudo cancelar la cita');

  const programada = cita.estado === 'programada';
  const asistenciaEditable = ['programada', 'realizada', 'no_asistio'].includes(cita.estado) && !cita.seguimiento_id;

  return (
    <>
      <div style={s.fondoModal} onClick={onCerrar} />
      <div style={s.modal} role="dialog" aria-label="Detalle de la cita">
        <div style={es.cabecera}>
          <h3 style={{ ...s.modalTitulo, margin: 0 }}>{cita.tipo || 'Cita'}</h3>
          <Chip def={ESTADO_CITA[cita.estado]} />
        </div>
        <div style={es.datos}>
          <div><strong>{fecha(cita.inicio)}</strong> · {hora(cita.inicio)}{cita.fin ? ` a ${hora(cita.fin)}` : ''}</div>
          {cita.estudiante && !desdeCaso && <div>Estudiante: {cita.estudiante}</div>}
          {cita.profesional && <div>Orientador: {cita.profesional}</div>}
          {cita.lugar && <div>Lugar: {cita.lugar}</div>}
          {cita.participantes?.length > 0 && <div>Participantes: {cita.participantes.join(', ')}</div>}
          {cita.asistencia && <div>Asistencia: {ASISTENCIA[cita.asistencia]}</div>}
          {cita.visible_familia && <div style={{ color: '#5a4fcf' }}>Visible para la familia</div>}
          {cita.seguimiento_id && <div style={{ color: '#2e7d32' }}>Tiene un seguimiento registrado</div>}
        </div>

        {gestionable && asistenciaEditable && !modo && (
          <div style={{ marginBottom: '14px' }}>
            <div style={s.label}>{programada ? '¿Cómo fue la cita?' : 'Corregir asistencia'}</div>
            <div style={es.botonera}>
              {Object.entries(ASISTENCIA).map(([v, n]) => (
                <button key={v} disabled={ocupado || cita.asistencia === v} onClick={() => marcar(v)}
                  style={{ ...s.btnSecundario, ...(cita.asistencia === v ? es.marcado : {}) }}>{n}</button>
              ))}
            </div>
          </div>
        )}

        {modo === 'reprogramar' && (
          <div style={es.caja}>
            <div style={es.fila2}>
              <div style={s.campo}>
                <label style={s.label}>Nueva fecha y hora</label>
                <input type="datetime-local" style={s.input} value={nuevoInicio} onChange={e => { setCruce(false); setNuevoInicio(e.target.value); }} />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Duración</label>
                <select style={s.input} value={duracion} onChange={e => setDuracion(e.target.value)}>
                  {DURACIONES.map(d => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60} h`}</option>)}
                </select>
              </div>
            </div>
            <p style={es.pista}>La fecha anterior queda registrada como “reprogramada”.</p>
            {cruce && (
              <div style={es.aviso}>
                Ese horario se cruza con otra cita programada.
                <button onClick={() => reprogramar(true)} disabled={ocupado} style={{ ...s.btnSecundario, marginTop: '8px', display: 'block' }}>Reprogramar de todos modos</button>
              </div>
            )}
            <div style={s.botones}>
              <button onClick={() => setModo(null)} style={s.btnSecundario}>Volver</button>
              <button onClick={() => reprogramar(false)} disabled={ocupado} style={s.btnPrimario}>Reprogramar</button>
            </div>
          </div>
        )}

        {modo === 'cancelar' && (
          <div style={es.caja}>
            <label style={s.label}>Motivo de la cancelación *</label>
            <textarea style={{ ...s.input, minHeight: '70px', resize: 'vertical' }} maxLength={1000} value={motivo} onChange={e => setMotivo(e.target.value)} />
            <div style={s.botones}>
              <button onClick={() => setModo(null)} style={s.btnSecundario}>Volver</button>
              <button onClick={cancelar} disabled={ocupado || !motivo.trim()} style={{ ...s.btnPeligro, opacity: motivo.trim() ? 1 : 0.5 }}>Cancelar cita</button>
            </div>
          </div>
        )}

        {error && <div style={s.errorBox}>{error}</div>}

        {!modo && (
          <div style={s.botones}>
            {cita.caso_id && !desdeCaso && (
              <button onClick={() => navigate(`/bienestar/casos/${cita.caso_id}`)} style={s.btnSecundario}>Ver caso</button>
            )}
            {gestionable && programada && <button onClick={() => setModo('cancelar')} style={s.btnSecundario}>Cancelar cita</button>}
            {gestionable && programada && <button onClick={() => setModo('reprogramar')} style={s.btnSecundario}>Reprogramar</button>}
            {cita.caso_id && cita.estado === 'realizada' && !cita.seguimiento_id && (
              <button onClick={() => { onCerrar(); navigate(`/bienestar/casos/${cita.caso_id}?cita=${cita.id}`); }} style={s.btnPrimario}>
                Registrar seguimiento
              </button>
            )}
            <button onClick={onCerrar} style={s.btnSecundario}>Cerrar</button>
          </div>
        )}
      </div>
    </>
  );
}

// Tarjeta compacta de una cita (agenda y pestaña del caso)
export function TarjetaCita({ cita, onClick, mostrarEstudiante = true }) {
  const def = ESTADO_CITA[cita.estado];
  const tachada = ['cancelada', 'reprogramada'].includes(cita.estado);
  return (
    <button onClick={onClick} style={{ ...es.tarjeta, borderLeft: `4px solid ${def?.color || '#ccc'}`, opacity: tachada ? 0.6 : 1 }}>
      <div style={es.tarjetaHora}>{hora(cita.inicio)}{cita.fin ? `–${hora(cita.fin)}` : ''}</div>
      <div style={{ ...es.tarjetaTitulo, textDecoration: tachada ? 'line-through' : 'none' }}>
        {mostrarEstudiante && cita.estudiante ? cita.estudiante : (cita.tipo || 'Cita')}
      </div>
      <div style={es.tarjetaMeta}>
        {mostrarEstudiante && cita.estudiante ? `${cita.tipo || ''}` : ''}{cita.lugar ? `${mostrarEstudiante && cita.estudiante ? ' · ' : ''}${cita.lugar}` : ''}
      </div>
      {cita.estado !== 'programada' && <div style={{ ...es.tarjetaEstado, color: def?.color }}>{def?.nombre}</div>}
    </button>
  );
}

const es = {
  contexto: { fontSize: '14px', color: '#555', margin: '0 0 12px' },
  fila2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '12px' },
  check: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#555', marginBottom: '14px', cursor: 'pointer', lineHeight: 1.4 },
  aviso: { background: '#fff8e1', color: '#8a5a00', borderRadius: '8px', padding: '10px 14px', fontSize: '13.5px', marginBottom: '14px' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  datos: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', color: '#444', marginBottom: '16px', lineHeight: 1.5 },
  botonera: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  marcado: { background: '#e8f5e9', color: '#2e7d32' },
  caja: { background: '#fafbff', border: '1px solid #e6e8f5', borderRadius: '12px', padding: '14px', marginBottom: '14px' },
  pista: { fontSize: '12px', color: '#999', margin: '0 0 8px' },
  tarjeta: { display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #eef0f5', borderRadius: '10px', padding: '8px 10px', cursor: 'pointer', fontFamily: 'inherit' },
  tarjetaHora: { fontSize: '11.5px', fontWeight: 700, color: '#667eea' },
  tarjetaTitulo: { fontSize: '13px', fontWeight: 700, color: '#1a1a2e', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis' },
  tarjetaMeta: { fontSize: '11.5px', color: '#999', marginTop: '1px' },
  tarjetaEstado: { fontSize: '11px', fontWeight: 700, marginTop: '2px' },
};
